import { ApolloClient, InMemoryCache, gql } from "@apollo/client/core";
import { ApolloLink, split } from "@apollo/client/link";
import { HttpLink } from "@apollo/client/link/http";
import { ErrorLink } from "@apollo/client/link/error";
import { SetContextLink } from "@apollo/client/link/context";
import { getMainDefinition } from "@apollo/client/utilities";
import { CombinedGraphQLErrors } from "@apollo/client/errors";
import { Observable } from "rxjs";
import { env } from "../config/env";
import { createGraphqlWsLink } from "./graphql-ws-link";

/**
 * Get the auth token from the Zustand auth store via localStorage.
 * Direct localStorage read avoids circular dependency with auth store module.
 */
function getAccessToken(): string | null {
  try {
    const stored = localStorage.getItem("auth-storage");
    if (!stored) return null;
    const parsed: unknown = JSON.parse(stored);
    if (
      typeof parsed === "object" &&
      parsed !== null &&
      "state" in parsed &&
      typeof (parsed as Record<string, unknown>)["state"] === "object"
    ) {
      const state = (parsed as Record<string, Record<string, unknown>>)["state"];
      if (state && typeof state["accessToken"] === "string") {
        return state["accessToken"];
      }
    }
    return null;
  } catch {
    return null;
  }
}

function getRefreshToken(): string | null {
  try {
    const stored = localStorage.getItem("auth-storage");
    if (!stored) return null;
    const parsed: unknown = JSON.parse(stored);
    if (
      typeof parsed === "object" &&
      parsed !== null &&
      "state" in parsed &&
      typeof (parsed as Record<string, unknown>)["state"] === "object"
    ) {
      const state = (parsed as Record<string, Record<string, unknown>>)["state"];
      if (state && typeof state["refreshToken"] === "string") {
        return state["refreshToken"];
      }
    }
    return null;
  } catch {
    return null;
  }
}

// Refresh mutation
const REFRESH_TOKENS_MUTATION = gql`
  mutation RefreshTokens($input: RefreshTokensInput!) {
    refreshTokens(input: $input) {
      accessToken
      refreshToken
      tokenType
      expiresIn
      user {
        id
        email
        name
      }
    }
  }
`;

// HTTP Link
const httpLink = new HttpLink({
  uri: env.apiBaseUrl,
});

// Auth Link — injects Bearer token (Apollo Client 4.x SetContextLink API)
const authLink = new SetContextLink((prevContext) => {
  const token = getAccessToken();
  const headers = (prevContext["headers"] as Record<string, string> | undefined) ?? {};
  return {
    headers: {
      ...headers,
      ...(token ? { authorization: `Bearer ${token}` } : {}),
    },
  };
});

// WebSocket Link for subscriptions (standard graphql-ws protocol)
const { link: wsLink, wsClient } = createGraphqlWsLink(env.wsUrl, getAccessToken);

// Track refresh state for concurrent protection
let isRefreshing = false;
let pendingRefreshSubscribers: ((token: string) => void)[] = [];

function addRefreshSubscriber(callback: (token: string) => void) {
  pendingRefreshSubscribers.push(callback);
}

function onRefreshComplete(newToken: string) {
  pendingRefreshSubscribers.forEach((cb) => cb(newToken));
  pendingRefreshSubscribers = [];
}

// Error Link — handles error categorization and auth refresh
const errorLink = new ErrorLink(({ error, operation, forward }) => {
  // Check for auth errors
  let isAuthError = false;

  if (CombinedGraphQLErrors.is(error)) {
    isAuthError = error.errors.some((err) => {
      const code = err.extensions?.["code"];
      return code === "UNAUTHENTICATED" || code === "TOKEN_EXPIRED";
    });
  } else if (
    error &&
    typeof error === "object" &&
    "statusCode" in error &&
    (error as { statusCode: number }).statusCode === 401
  ) {
    isAuthError = true;
  }

  if (isAuthError) {
    const refreshToken = getRefreshToken();

    if (refreshToken && !isRefreshing) {
      isRefreshing = true;

      return new Observable((observer) => {
        apolloClient
          .mutate({
            mutation: REFRESH_TOKENS_MUTATION,
            variables: { input: { refreshToken } },
          })
          .then((result) => {
            const data = result.data as {
              refreshTokens?: { accessToken: string; refreshToken: string };
            } | null;

            if (data?.refreshTokens) {
              // Update localStorage directly
              try {
                const stored = localStorage.getItem("auth-storage");
                if (stored) {
                  const parsed = JSON.parse(stored) as Record<string, unknown>;
                  (parsed as Record<string, Record<string, unknown>>)["state"] = {
                    ...((parsed as Record<string, Record<string, unknown>>)["state"] ?? {}),
                    accessToken: data.refreshTokens.accessToken,
                    refreshToken: data.refreshTokens.refreshToken,
                  };
                  localStorage.setItem("auth-storage", JSON.stringify(parsed));
                }
              } catch {
                // Ignore storage errors
              }

              isRefreshing = false;
              onRefreshComplete(data.refreshTokens.accessToken);

              // Retry the failed operation
              const sub = forward(operation).subscribe({
                next: (value) => observer.next(value),
                error: (err: unknown) => observer.error(err),
                complete: () => observer.complete(),
              });

              return () => sub.unsubscribe();
            } else {
              // Refresh failed — clear auth
              isRefreshing = false;
              localStorage.removeItem("auth-storage");
              observer.error(error);
              return undefined;
            }
          })
          .catch(() => {
            isRefreshing = false;
            localStorage.removeItem("auth-storage");
            observer.error(error);
          });
      });
    } else if (refreshToken && isRefreshing) {
      // Queue this request to retry after in-flight refresh
      return new Observable((observer) => {
        addRefreshSubscriber(() => {
          const sub = forward(operation).subscribe({
            next: (value) => observer.next(value),
            error: (err: unknown) => observer.error(err),
            complete: () => observer.complete(),
          });
          return () => sub.unsubscribe();
        });
      });
    }
  }

  // Non-auth errors pass through
  return undefined;
});

// Split link — route subscriptions to WS, queries/mutations to HTTP
const splitLink = split(
  ({ query }) => {
    const def = getMainDefinition(query);
    return def.kind === "OperationDefinition" && def.operation === "subscription";
  },
  wsLink,
  authLink.concat(httpLink),
);

// Compose the link chain: ErrorLink → SplitLink
const link = ApolloLink.from([errorLink, splitLink]);

// Apollo Client instance
export const apolloClient = new ApolloClient({
  link,
  cache: new InMemoryCache(),
  defaultOptions: {
    watchQuery: {
      fetchPolicy: "cache-and-network",
    },
  },
});

// Export for WebSocket lifecycle management
export { wsClient };
